<script>
  export default {
    name: 'GroupSelect',

    props: {
      label: {
        type: String,
        required: true,
      },
      items: {
        type: Array,
        required: true,
      },
      value: {
        default: null,
      },
    },

    data: () => ({
      menu: false,
      selectedItem: null,
      searchQuery: '',
    }),

    watch: {
      value: {
        immediate: true,
        handler(value) {
          this.selectedItem = value;
        },
      },
    },

    computed: {
      filteredItems() {
        if (!this.searchQuery) {
          return this.items;
        }

        const query = this.searchQuery.toLowerCase();

        return this.items
          .map((group) => ({
            ...group,
            items: group.items.filter(
              (item) => item.text.toLowerCase().includes(query) || item.subtitle.toLowerCase().includes(query),
            ),
          }))
          .filter((group) => group.items.length > 0);
      },
    },

    methods: {
      selectItem(currency) {
        this.selectedItem = currency;
        this.$emit('input', currency);
        this.menu = false;

        this.$emit('select', currency);
      },
      getIconUrl(icon) {
        if (icon.includes('/images/coins')) {
          return icon;
        }

        return `/images/coins/${icon}?v=3`;
      },
    },
  };
</script>

<template>
  <v-menu v-model="menu" :close-on-content-click="false" attach content-class="grouped-menu" offset-y>
    <template #activator="{ on, attrs }">
      <v-btn
        class="grouped-menu-btn elevation-8"
        color="main-light"
        dark
        height="42"
        v-bind="attrs"
        width="100%"
        v-on="on"
      >
        <div class="d-flex justify-space-between w-full">
          <div v-if="!selectedItem" class="select-label">
            {{ label }}
          </div>
          <div v-else class="selected-item">
            <div class="list-icon-wrapper">
              <img :src="getIconUrl(selectedItem.icon)" alt="list-icon" class="list-icon" />
            </div>
            <span class="list-text__title">{{ selectedItem.text }}</span>
          </div>

          <v-icon v-if="menu">mdi-chevron-up</v-icon>
          <v-icon v-else>mdi-chevron-down</v-icon>
        </div>
      </v-btn>
    </template>

    <div class="search-container">
      <v-text-field
        v-model="searchQuery"
        class="search-input"
        clearable
        hide-details
        label="Search"
        prepend-inner-icon="mdi-magnify"
        solo
      />
    </div>

    <div class="menu-content">
      <template v-if="filteredItems.length === 0">
        <div class="no-data">No data available</div>
      </template>
      <template v-else>
        <div v-for="(group, i) in filteredItems" :key="i">
          <div class="group-title">Choose your {{ group.text.toLowerCase() }}</div>
          <ul>
            <li v-for="item in group.items" :key="item.value" @click="selectItem(item)">
              <div class="list-icon-wrapper">
                <img v-reload-on-fail :src="getIconUrl(item.icon)" alt="list-icon" class="list-icon" />
              </div>
              <div class="list-text">
                <span class="list-text__title font-weight-600">{{ item.text }}</span>
                <span class="list-text__subtitle">{{ item.subtitle }}</span>
              </div>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </v-menu>
</template>

<style lang="scss" scoped>
  .search-container {
    position: sticky;
    top: 0;
    padding-top: 8px;
    background-color: #24073d !important;
    z-index: 1;
  }

  .no-data {
    font-size: 20px;
    font-weight: 600;
    width: 100%;
    text-align: center;
    margin: 10px 0;
    color: white;
  }

  .search-input {
    height: 43px;
    margin: 0 16px;

    &::v-deep {
      .v-input__slot {
        border-radius: 5px;
        font-weight: 600;
        font-size: 20px;
        background: #36214d !important;

        .v-input__prepend-inner {
          margin-top: 4px !important;
        }

        .v-icon {
          color: rgba(255, 255, 255, 0.5) !important;
        }

        .v-text-field__slot {
          input,
          label {
            padding: unset !important;
            color: white;
          }
        }
      }
    }
  }

  .grouped-menu-btn {
    background: #24073d !important;
    text-transform: unset;
    font-weight: 800;
    font-size: 16px !important;
  }

  .selected-item {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: start;
    gap: 16px;

    .list-icon-wrapper {
      width: 28px;
      display: flex;
      justify-content: center;
      align-items: center;

      .list-icon {
        font-size: 24px;
        color: white;
        height: 28px;
      }
    }
  }

  .grouped-menu {
    min-width: unset !important;
    background-color: #24073d;
    max-height: 340px;
    overflow-y: auto;
    margin-top: 6px;

    @media screen and (max-height: 450px) {
      max-height: 260px !important;
    }

    @media screen and (max-width: 480px) {
      max-width: 75vw;
    }

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-track {
      background: var(--v-main-base);
    }

    &::-webkit-scrollbar-thumb {
      background-color: white;
      border-radius: 10px;
      border: 3px solid transparent;
    }

    .group-title {
      margin: 12px 8px;
      padding-left: 10px;
      color: #e7e5ec;
      font-size: 18px;
      font-weight: 700;
      line-height: 120%;
    }

    ul {
      padding: 0;

      li {
        padding-left: 16px;
        margin: 6px 0;
        height: 34px;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s;
        position: relative;
        cursor: pointer;

        .list-icon-wrapper {
          width: 28px;
          display: flex;
          justify-content: center;
          align-items: center;

          .list-icon {
            font-size: 24px;
            color: white;
            height: 28px;
          }
        }

        .list-text {
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;

          &__title {
            font-size: 21px;
            margin-top: 3.5px;
            line-height: 13px;
          }

          &__subtitle {
            margin-top: 4px;
            font-size: 11px;
          }
        }

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-size: 200% 100%;
          background: linear-gradient(to right, rgba(255, 255, 255, 0.3) 10%, var(--v-main-mid-base));
          opacity: 0;
          transition: opacity 0.3s;
          z-index: -1;
        }

        &:hover::before {
          opacity: 1;
        }

        &:hover {
          box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.5);
        }
      }
    }
  }

  .select-label {
    font-size: 16px;
    font-weight: 500;
    line-height: 150%;
  }

  .menu-content {
    width: 349px;
  }
</style>
